<template>
  <div>
    <div v-if="!isTeemill" class="row">
      <div class="col-12">
        <component :is="maxDevice('md') ? 'h4' : 'h1'">
          <b>
            {{ $route.meta.title }}
          </b>
        </component>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <template v-if="urlQuestion">
          <help-question-list
            :question-list="urlQuestion"
            @not-arrived="onNotArrived"
            @unhelpful="$event => $emit('unhelpful', $event)"
          />
        </template>
        <tml-form form-name="help-browser-search-form" class="pb-4">
          <tml-input
            :value="questionSearch"
            class="mb-0"
            :icon="faMagnifyingGlass"
            placeholder="Search FAQs"
            field-name="search-input"
            @input="$router.replace({query: {...$route.query, search: $event}})"
            @submit="
              $router.replace({query: {...$route.query, search: $event}})
            "
          />
        </tml-form>
        <div v-if="showSearch">
          <tml-spinner v-if="newQuestionSearchLoading" />
          <help-question-list
            v-else
            :question-list="isSubdomain ? newSearchResults : filteredSearch"
            multiple
            @not-arrived="onNotArrived"
            @unhelpful="$event => $emit('unhelpful', $event)"
          />
          <div v-if="showNone">
            <h4 class="md-2">Sorry, no questions found</h4>
          </div>
          <tml-button
            v-show="showSearchButton"
            v-scroll-to="{
              el: '#top5',
              offset: -80,
            }"
            class="md-2 mb-8 spaced"
            text="Can't find what you are looking for?"
            @click="onSearchButtonClick"
          />
        </div>
        <tml-grid v-if="groups" :breakpoints="{md: 3, lg: 6}">
          <tml-new-pod
            v-for="(group, order) in groups"
            :key="order"
            :title="group.name"
            :icon="group.data"
            horizontal-alignment="center"
            :icon-size="2"
            @click="
              $router.replace({query: {...$route.query, group: group.code}})
            "
          />
          <tml-new-pod
            v-if="isTeemill && showTutorialsPod"
            title="Tutorials"
            :icon="faGraduationCap"
            horizontal-alignment="center"
            href="/academy/"
            :icon-size="2"
          />
        </tml-grid>
        <div id="search" />
        <div v-if="showGroupedQuestions" class="mt-12">
          <h3>Questions about {{ group }}</h3>
          <tml-spinner v-if="newQuestionsLoading" />
          <help-question-list
            v-else
            :question-list="groupsQuestions"
            multiple
            @not-arrived="onNotArrived"
            @unhelpful="$event => $emit('unhelpful', $event)"
          />
        </div>
        <div v-if="showGroupNone">
          <h4 class="md-2">Sorry, no questions found</h4>
        </div>
        <tml-button
          v-show="showGroupSearchButton"
          v-scroll-to="{
            el: '#top5',
            offset: -50,
          }"
          class="md-2 spaced"
          text="Can't find what you are looking for?"
          @click="onGroupSearchButtonClick"
        />
        <div id="top5" />
        <div v-if="showTop5">
          <h3 class="mt-12">Top 5 FAQs</h3>
          <help-question-list
            :question-list="popularQuestions"
            multiple
            @not-arrived="onNotArrived"
            @unhelpful="$event => $emit('unhelpful', $event)"
          />
          <div class="spaced" />
          <tml-button
            v-show="showTop5Button"
            v-scroll-to="{
              el: '#topic',
            }"
            class="md-2"
            text="Still can't find what you are looking for?"
            @click="(showTopic = true), (showTop5Button = false)"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div id="topic" />
        <div v-if="showTopic">
          <tml-new-form name="topic-form">
            <h3 class="md-2 spaced">Could you tell us a bit more?</h3>
            <tml-new-select
              v-model="selected"
              class="md-2"
              name="formSelect"
              :options="reasons"
              clearable
              none-selected="No topic selected"
              label="Topic"
              max-height="190"
              @input="onReason"
            />
          </tml-new-form>
          <div class="md-2">
            <help-amend
              v-if="reasonCode === 'can_i_amend_my_order'"
              @amend-order-value="amendOrderValue"
            />
            <help-approval v-if="reasonCode === 'product_not_approved'" />
            <help-wrong-amount v-if="reasonCode === 'paid_wrong_amount'" />
            <help-track-order
              v-if="reasonCode === 'where_is_my_order'"
              @not-arrived="onNotArrived"
            />
            <help-return-order-tracking
              v-if="reasonCode === 'have_you_received_my_return'"
            />
            <help-copyright
              v-if="
                reasonCode === 'teemill_frontend_copyright' &&
                $store.state.subdomain?.platform?.id === 15
              "
            />
          </div>
        </div>
        <div id="contact-form" />
        <tml-contact-form
          v-if="showContact"
          :search-terms="allTerms"
          :reason="selected"
          @contact-submitted="
            showTopic = false;
            showContact = false;
            showTop5 = false;
            showGroupedQuestions = false;
          "
        />
      </div>
    </div>
  </div>
  <contact-information v-if="groupsLoaded" />
</template>

<script>
import Vue from 'vue';
import {computed} from 'vue';
import {useHead} from '@vueuse/head';
import VueScrollTo from 'vue-scrollto';
import {Searchable} from '@teemill/common/classes';
import {formatUrl, maxDevice} from '@teemill/common/helpers';
import {debounce, uniqBy} from 'lodash';
import {
  faMagnifyingGlass,
  faGraduationCap,
} from '@fortawesome/pro-light-svg-icons';
import ContactInformation from './ContactInformation.vue';
import HelpAmend from './HelpAmend.vue';
import HelpApproval from './HelpApproval.vue';
import HelpQuestionList from './HelpQuestionList.vue';
import HelpWrongAmount from './HelpWrongAmount.vue';
import HelpTrackOrder from './HelpTrackOrder.vue';
import HelpReturnOrderTracking from './HelpReturnOrderTracking.vue';
import HelpCopyright from './HelpCopyright.vue';
Vue.use(VueScrollTo);

export default {
  name: 'HelpBrowser',

  components: {
    ContactInformation,
    HelpAmend,
    HelpApproval,
    HelpWrongAmount,
    HelpTrackOrder,
    HelpReturnOrderTracking,
    HelpQuestionList,
    HelpCopyright,
  },

  inject: ['app'],

  props: {
    faqCode: String,

    contactFormCode: String,

    isTeemill: {
      type: Boolean,
      default: false,
    },

    showTutorialsPod: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['unhelpful'],

  data() {
    return {
      faGraduationCap,
      faMagnifyingGlass,

      // Question data
      urlQuestion: null,
      popularQuestions: [],
      questions: [],
      newQuestions: [
        {
          question: 'Where is my order?',
          group: {
            name: 'My Order',
          },
          url: 'where-is-my-order',
        },
        {
          question: 'How do I edit my order?',
          group: {
            name: 'My Order',
          },
          url: 'how-do-i-edit-my-order',
        },
        {
          question: 'How do I cancel my order?',
          group: {
            name: 'My Order',
          },
          url: 'how-do-i-cancel-my-order',
        },
        {
          question: 'How do I return my order?',
          group: {
            name: 'My Order',
          },
          url: 'how-do-i-return-my-order',
        },
      ],
      groupsQuestions: [],
      questionsLoaded: false,
      question: {},
      newQuestionsLoading: false,
      newSearchResults: [],
      newQuestionSearchLoading: false,

      // Search data
      questionSearch: '',
      searchableProperties: {
        question: 3,
        tag: 3,
        answer: 1,
      },
      allTerms: [],
      search: [],

      // Contact reason data
      reasons: [],
      reasonsLoaded: false,
      reasonForm: '',
      reasonCode: '',

      // Group data
      group: null,
      groups: [],
      groupsLoaded: false,

      // Form data
      selected: null,
      submitLoading: false,

      // HTML show booleans
      showTop5: false,
      showTopic: false,
      showContact: false,
      showSearchButton: false,
      showGroupSearchButton: false,
      showTop5Button: false,
      showSearch: false,
      showGroupedQuestions: false,
      showNone: false,
      showGroupNone: false,

      // Computed property data
      limit: 10,
    };
  },

  computed: {
    /**
     * Filter search results to the value of the limit variable
     */
    filteredSearch() {
      return this.search ? this.search.slice(0, this.limit) : null;
    },

    project() {
      return (
        this.$project?.active?.id || this.$store.state.subdomain?.project?.id
      );
    },

    isSubdomain() {
      return this.app.isSubdomain;
    },
  },

  watch: {
    '$route.query.group': {
      handler(group) {
        if (group) {
          this.searchByGroup(group);
        }
      },
    },

    '$route.query.search': {
      handler(search) {
        this.attemptSearch();
      },
    },

    questionsLoaded() {
      this.attemptSearch();
    },

    reasonsLoaded() {
      if (this.$route.params.reasonName) {
        this.setReasonFromRoute();
      }
    },

    groupsLoaded: {
      immediate: true,
      handler(value, oldValue) {
        if (value && !oldValue && this.$route.query.group) {
          this.searchByGroup(this.$route.query.group);
        }
      },
    },

    $route() {
      this.attemptSearch();
    },

    '$route.params': {
      handler(params) {
        this.showTopic = false;
        this.showContent = false;
        this.selected = null;
        this.urlQuestion = null;
        this.showGroupedQuestions = false;
        this.showSearch = false;
        this.questionSearch = this.$route.query.search || '';
        this.showTop5 = false;

        if (params.questionName && (this.isSubdomain || this.questionsLoaded)) {
          this.setQuestionFromRoute();
        }

        if (params.reasonName && this.reasonsLoaded) {
          this.setReasonFromRoute();
        }
      },
      immediate: true,
    },
  },

  created() {
    this.debouncedCallSearch = debounce(this.callSearch, 300);
    this.debouncedCallNewSearch = debounce(this.callNewSearch, 300);

    this.setMetaData();

    // backwards compatibility for legacy /help/group/{groupCode} routes
    if (this.$route.params.groupCode && !this.$route.query.group) {
      this.$router.replace({
        query: {...this.$route.query, group: this.$route.params.groupCode},
      });
    }

    if (this.$route.query.search) {
      this.attemptSearch();
    }

    if (this.isSubdomain) {
      this.getNewGroups();
      this.getNewPopularQuestions();
    } else {
      this.getGroups();
      this.getQuestions();
      this.getPopularQuestions();
    }
    this.getContactReasons();
  },

  methods: {
    maxDevice,

    getQuestions() {
      this.axios
        .get(formatUrl('/omnis/v3/contact/get-faqs/'), {
          params: {
            faq_code: this.faqCode,
          },
        })
        .success(data => {
          this.questions = data;

          if (this.faqCode === 'teemill' || this.faqCode === 'customer') {
            this.questions.push({
              question: 'How do I cancel my order?',
              url: 'how-do-i-cancel-my-order',
            });
          }

          if (this.faqCode === 'customer') {
            this.questions.push(
              {
                question: 'Where is my order?',
                url: 'where-is-my-order',
              },
              {
                question: 'How do I return my order?',
                url: 'how-do-i-return-my-order',
              },
              {
                question: 'How do I edit my order?',
                url: 'how-do-i-edit-my-order',
              },
              {
                question: 'How do I cancel my order?',
                url: 'how-do-i-cancel-my-order',
              },
              {
                question:
                  "I'd like to report an Intellectual Property/Copyright Infringement",
                url: 'id-like-to-report-an-intellectual-property-copyright-infringement',
              }
            );
          }

          this.setQuestionFromRoute();

          this.questionsLoaded = true;
        })
        .oops();
    },

    getGroups() {
      this.axios
        .get(formatUrl('/omnis/v3/contact/get-groups/'), {
          params: {
            faq_code: this.faqCode,
          },
        })
        .then(({data}) => {
          this.groups = Object.values(data).sort((a, b) => a.order - b.order);
          this.groupsLoaded = true;
        });
    },

    getNewGroups() {
      this.axios
        .get(formatUrl('/omnis/v3/contact/new-faq-groups/'), {
          params: {
            project: this.project,
            search: `visibility:storefront`,
          },
        })
        .then(({data}) => {
          this.groups = data.map(group => ({
            name: group.name,
            code: group.code,
            data: group.icon,
          }));
          this.groupsLoaded = true;
        });
    },

    async getNewQuestions(groupCode) {
      this.newQuestionsLoading = true;

      const {data} = await this.axios.get(
        formatUrl('/omnis/v3/contact/new-faqs/'),
        {
          params: {
            project: this.project,
            search: `visibility:storefront group:${groupCode}`,
          },
        }
      );

      this.newQuestionsLoading = false;

      return uniqBy(data, 'question');
    },

    getPopularQuestions() {
      this.axios
        .get(formatUrl('/omnis/v3/contact/popular-faqs/'), {
          params: {
            faq_code: this.faqCode,
          },
        })
        .then(({data}) => {
          this.popularQuestions = data;
        });
    },

    getNewPopularQuestions() {
      this.axios
        .get(formatUrl('/omnis/v3/contact/new-faqs/'), {
          params: {
            project: this.project,
            pageSize: 5,
            search: `visibility:storefront`,
            sortBy: ['-upVotes'],
          },
        })
        .then(({data}) => {
          this.popularQuestions = data;
        });
    },

    getContactReasons() {
      this.axios
        .get(formatUrl('/omnis/v3/contact/reason/'), {
          params: {
            form_code: this.contactFormCode,
          },
        })
        .then(({data}) => {
          data.forEach(reason => {
            if (
              reason.code === 'teemill_frontend_copyright' &&
              this.$store.state.subdomain?.platform?.id !== 15
            ) {
              return;
            }

            this.reasons.push({
              text: reason.reason,
              value: reason.id,
              code: reason.code,
            });
          });
          this.reasonsLoaded = true;
        });
    },

    onSearchButtonClick() {
      this.showTop5 = true;
      this.showSearchButton = false;
      this.showGroupSearchButton = false;
      this.showTop5Button = true;
    },

    onGroupSearchButtonClick() {
      this.showTop5 = true;
      this.showSearchButton = false;
      this.showGroupSearchButton = false;
      this.showTop5Button = true;
    },

    /**
     * When one of the pallette tiles is selected, pass through the group id and perform an axios request to get all the questions relating to that group
     */
    async searchByGroup(groupCode) {
      const group = this.groups.find(g => g.code === groupCode);

      if (!group) {
        this.searchingByGroup = false;
        return;
      }

      this.showGroupedQuestions = true;
      this.groupsQuestions = [];
      this.group = group.name;

      if (this.isSubdomain) {
        this.newQuestions = await this.getNewQuestions(groupCode);

        Object.keys(this.newQuestions).forEach(key => {
          if (this.newQuestions[key].group.name === group.name) {
            this.groupsQuestions.push(this.newQuestions[key]);
          }
        });

        this.showGroupSearchButton = true;
        this.showTop5 = false;
        this.showTopic = false;
        this.showContact = false;

        // Set a timeout before scrolling to ensure consistent page size once scrollTo is called
        setTimeout(() => {
          VueScrollTo.scrollTo('#search', {offset: -40});
        }, 0);
      } else {
        Object.keys(this.questions).forEach(key => {
          if (this.questions[key].group_id === group.id) {
            this.groupsQuestions.push(this.questions[key]);
          }
        });

        /**
         * If group is 'my order' and FAQs are for customers, add 'Where is my order?' to list of quesions
         */
        if (this.groupsQuestions && this.groupsQuestions[0]) {
          if (
            this.groupsQuestions[0].group_id === 1 &&
            this.faqCode === 'customer'
          ) {
            this.groupsQuestions.unshift({
              question: 'How do I cancel my order?',
            });

            this.groupsQuestions.unshift({
              question: 'How do I edit my order?',
            });

            this.groupsQuestions.unshift({
              question: 'How do I return my order?',
            });

            this.groupsQuestions.unshift({
              question: 'Where is my order?',
            });
          }

          if (
            this.groupsQuestions[0].group_id === 4 &&
            this.faqCode === 'customer'
          ) {
            this.groupsQuestions.unshift({
              question: 'How do I return my order?',
            });
          }

          if (this.groupsQuestions[0].group_id === 11) {
            this.groupsQuestions.unshift({
              question:
                "I'd like to report an Intellectual Property/Copyright Infringement",
            });
          }
        }

        this.showGroupSearchButton = true;
        this.showTop5 = false;
        this.showTopic = false;
        this.showContact = false;

        // Set a timeout before scrolling to ensure consistent page size once scrollTo is called
        setTimeout(() => {
          VueScrollTo.scrollTo('#search', {offset: -40});
        }, 0);
      }
    },

    /**
     * When the user clicks the "still not arrived?" link on order tracking, show the contact form
     */
    onNotArrived() {
      this.showContact = true;
    },

    onSearchInput(query) {
      if (!this.isSubdomain) {
        this.debouncedCallSearch(query);
      } else {
        this.debouncedCallNewSearch(query);
      }
    },

    callNewSearch(query) {
      if (!this.isSubdomain || !query) {
        return;
      }

      const searchQuery = `${query} visibility:storefront`;

      this.showNone = false;
      this.showTop5 = false;
      this.showTopic = false;
      this.showContact = false;

      this.newQuestionSearchLoading = true;

      this.axios
        .get(formatUrl('/omnis/v3/contact/new-faqs/'), {
          params: {
            project: this.project,
            search: searchQuery,
          },
        })
        .then(({data}) => {
          this.newSearchResults = uniqBy(data, 'question');

          if (!this.newSearchResults.length) {
            this.showNone = true;
          }

          this.newQuestionSearchLoading = false;
        });

      this.showSearch = true;
      this.showSearchButton = true;
    },

    callSearch(query) {
      if (this.isSubdomain) {
        return;
      }

      const searchable = new Searchable(
        this.questions,
        this.searchableProperties
      );
      searchable.query(query).then(result => {
        this.search = result;
        this.showSearch = true;
        this.showSearchButton = true;
        this.showTop5 = false;
        this.showTopic = false;
        this.showContact = false;
        if (query === '') {
          this.showSearchButton = false;
          this.search = '';
        }
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        this.timer = setTimeout(() => {
          this.allTerms.push(query);
        }, 750);
        this.allTerms = this.allTerms.filter(
          term => term !== null && term.length >= 3
        );
        if (this.search.length === 0 && query !== '') {
          this.showNone = true;
        } else {
          this.showNone = false;
        }
      });
    },

    onReason($event) {
      this.reasonCode = $event?.code;

      if (
        this.reasonCode === 'report_offensive_product' ||
        this.reasonCode === 'paid_wrong_amount' ||
        this.reasonCode === 'report_bug' ||
        this.reasonCode === 'feedback' ||
        this.reasonCode === 'something_else' ||
        this.reasonCode === 'press_enquiry'
      ) {
        this.showContact = true;
      } else {
        this.showContact = false;
      }
    },

    amendOrderValue(amendOrderValue) {
      if (amendOrderValue === 2) {
        this.showContact = true;
      }
    },

    attemptSearch() {
      if (this.$route.query.search) {
        this.questionSearch = this.$route.query.search;

        this.onSearchInput(this.questionSearch);
      }
    },

    escapeQuotes(answer) {
      return answer.replace(/"/g, '\\"');
    },

    setMetaData() {
      useHead(
        computed(() => {
          if (this.questions.length === 0) {
            return {};
          }

          // only include questions with answers
          const filteredQuestions = this.questions.filter(
            question => question.answer
          );

          const faqMarkupArray = filteredQuestions.map(
            question => `{
      "@type": "Question",
      "name": "${question.question}",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "${this.escapeQuotes(question.answer)}"
      }
    }`
          );

          const faqMarkup = `[${faqMarkupArray.join(',')}]`;

          return {
            script: [
              {
                type: 'application/ld+json',
                children: `
            {
              "@context": "http://schema.org/",
              "@type": "FAQPage",
              "mainEntity": ${faqMarkup}
            }
          `,
              },
            ],
          };
        })
      );
    },

    setQuestionFromRoute() {
      if (this.isSubdomain) {
        Object.keys(this.newQuestions).forEach(key => {
          if (this.newQuestions[key].url === this.$route.params.questionName) {
            this.urlQuestion = this.newQuestions[key];
          }
        });
      } else {
        Object.keys(this.questions).forEach(key => {
          if (this.questions[key].url === this.$route.params.questionName) {
            this.urlQuestion = this.questions[key];
          }
        });
      }
    },

    setReasonFromRoute() {
      this.showTopic = true;

      if (
        this.$route.params.reasonName === 'report' &&
        this.$store.state.subdomain?.platform?.id === 15
      ) {
        this.selected = {
          selected: true,
          text: "I'd like to report an Intellectual Property / Copyright Infringement",
          code: 'teemill_frontend_copyright',
          value: 20,
        };
      } else {
        this.selected = this.reasons.find(
          reason =>
            reason.code === this.$route.params.reasonName.replace(/-/g, '_')
        );
      }

      // Set a timeout before scrolling to ensure consistent page size once scrollTo is called
      setTimeout(() => {
        VueScrollTo.scrollTo('#topic');
      }, 100);
    },
  },
};
</script>

<style lang="scss" scoped>
p {
  margin-top: 1em;
  margin-bottom: 1em;
}

.spaced {
  margin-top: 70px;
}
</style>
